import { useTheme as styledTheme } from "styled-components";
import { useState, memo } from "react";
import Select, { MultiValue, SingleValue } from "react-select";
import {
  GalleryData,
  Links,
  ProjectDataProps,
  ProjectFormComponetProps,
  SelectOption,
} from "../../../common/types";
import { ErrorMessage, inputContainerStyle, InputStyled } from "../contactForm";
import {
  getCustomStylesMultiSelect,
  projectLinkOptions, 
} from "../../../../utils/CustomizedLibraries/CustomSelectInput";
import { Squarebtn } from "../../../common/squareBtnProps";
import { handleProjectFormBlur, ProjectvalidateField } from "../../../../utils/Validations/adminFormValidationUtil";

const LinksInput: React.FC<ProjectFormComponetProps> = ({
  projectData,
  errors,
  touched,
  setErrors,
  setProjectData,
  setTouched,
  showAlert,
}) => {
  const styledComponentTheme = styledTheme();

  //selected links state
  const [selectedLinkOptions, setSelectedLinkOptions] = useState<
    MultiValue<SelectOption>
  >([]);


  // Handle link selection
  const handleLinkSelectChange = async (
    newOptions: MultiValue<SelectOption> | null
  ) => {
    // Creating a mutable array for updating state from readonly array
    const updatedSelectedOptions = newOptions ? [...newOptions] : [];

    // Determine which option(s) are being removed
    const removedOptions = selectedLinkOptions.filter(
      (prevSelected) =>
        !updatedSelectedOptions.some(
          (newOption) => newOption.value === prevSelected.value
        )
    );

    for (const option of removedOptions) {
      const linkValue = projectData.links[option.value as keyof Links];
      const inValidLink = errors.links?.[option.value as keyof Links];
      // Construct message based on link validity and existence
      const message = linkValue
        ? inValidLink
          ? `The link "${linkValue}" for "${option.label}" is invalid. Removing this option will clear the input. Do you still want to proceed?`
          : `The link "${linkValue}" for "${option.label}" is valid. Removing this option will clear the input. Are you sure you want to proceed?`
        : `Are you sure you want to remove "${option.label}"?`;

      //Await user's decision from alert
      const shouldRemove = await new Promise<boolean>((resolve) =>
        showAlert(true, `Deselect ${option.label}?`, message, true, undefined, () =>
          resolve(true)
        )
      );
      console.log("Deselect link resolve", shouldRemove);
      if (!shouldRemove) {
        return; // Exit if the user cancels
      }
    }

    //update state to remove option and clear associated inputs
    setSelectedLinkOptions(updatedSelectedOptions);
    setProjectData((prev) => {
      const updateLinks = { ...prev.links };
      removedOptions.forEach((option) => {
        updateLinks[option.value as keyof Links] = ""; //clear associated input
      });
      return {
        ...prev, //project data remains the same
        links: updateLinks,
      };
    });
  };

  // Handle updates for links
  const handleLinkChange = (linkType: keyof Links, value: string) => {
    setProjectData((prev) => ({
      ...prev,
      links: {
        ...prev.links,
        [linkType]: value,
      },
    }));

    // Pass the updated `links` object for validation, radther than passing projectData.links
    validateFieldWrapper("links", {
      [linkType]: value,
    });
  };

  //validations functions
  const handleBlurWrapper = (
    name: keyof ProjectDataProps,
    linkType: keyof Links,
    value: string
  ) => {
    handleProjectFormBlur(
      name,
      { [linkType]: value }, // Validate only the specific link field
      touched,
      setTouched,
      validateFieldWrapper
    );
  };

  const validateFieldWrapper = (
    name: keyof ProjectDataProps,
    value:
      | string
      | SingleValue<SelectOption>
      | number
      | GalleryData
      | Partial<Links>
  ) => {
    ProjectvalidateField(name, value, errors, setErrors, projectData);
  };

  const isLinkValid = (linkType: keyof Links): boolean => {
    return (
      projectData.links[linkType]?.trim() !== "" && !errors.links?.[linkType]
    );
  };

  return (
    <div style={inputContainerStyle}>
      <label htmlFor="projectlinks">Projects links (Optional)</label>
      <Select
        inputId="projectlinks"
        value={selectedLinkOptions}
        onChange={handleLinkSelectChange}
        placeholder="Select link type"
        options={projectLinkOptions}
        styles={getCustomStylesMultiSelect(styledComponentTheme)}
        isSearchable={false}
        isMulti
      />
      {selectedLinkOptions.map((option) => (
        <div key={option.value} style={{ marginTop: "5px" }}>
          <label htmlFor={`projectlinks-${option.value}`}>{option.label}</label>
          <InputStyled
          id={`projectlinks-${option.value}`}
            type="url"
            autoComplete="url"
            name={`projectlinks-${option.value}`} // Unique name per input
            placeholder={`Enter ${option.label} URL`}
            value={projectData.links[option.value as keyof Links]}
            onChange={(e) =>
              handleLinkChange(option.value as keyof Links, e.target.value)
            }
            style={{ marginTop: "3px" }}
            onBlur={(e) =>
              handleBlurWrapper(
                "links",
                option.value as keyof Links,
                e.target.value
              )
            }
            $errorColor={errors.links?.[option.value as keyof Links] ? "red": ""}
          />
          {errors.links?.[option.value as keyof Links] && (
            <ErrorMessage>
              {errors.links[option.value as keyof Links]}
            </ErrorMessage>
          )}

          {isLinkValid(option.value as keyof Links) && (
            <Squarebtn
            type="button"
              onClick={() =>
                window.open(
                  projectData.links[option.value as keyof Links],
                  "_blank"
                )
              }
              $margin="8px 0 0 0"
              height="50px"
              $fontSize="12.5px"
            >
              Test {option.label}
            </Squarebtn>
          )}
        </div>
      ))}
    </div>
  );
};

export default memo(LinksInput);
